import { DeleteOutlined, WarningOutlined } from '@ant-design/icons';
import {
  getGetBackExportTemplateTypeQueryKey,
  useDeleteBackExportTemplateId,
} from '@framework/api-client/api/backoffice/export-template-controller/export-template-controller.ts';
import { type ExportTemplateDTOType } from '@framework/api-client/api/backoffice/schemas/exportTemplateDTOType.ts';
import { getNetworkErrorMessage } from '@framework/auth/http/errors/getNetworkErrorMessage.ts';
import { Button, Modal, message, Tooltip, theme } from 'antd';
import { type ButtonProps } from 'antd/lib';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';

const { useToken } = theme;

export interface IExportTemplateDeleteModalButtonProps extends ButtonProps {
  templateId?: string | null;
  isJustCreated?: boolean;
  iconOnlyButton?: boolean;
  exportTemplateType?: ExportTemplateDTOType;
  setSelectedTemplate: (value: string) => void;
  onDelete: (fields: string[]) => void;
}

/**
 * Pulsante per eliminare un template con conferma
 */
export function ExportTemplateDeleteModalButton(
  props: IExportTemplateDeleteModalButtonProps,
) {
  const {
    templateId,
    iconOnlyButton,
    isJustCreated,
    exportTemplateType,
    setSelectedTemplate,
    onDelete,
    ...otherProps
  } = props;

  const { token } = useToken();
  const { t } = useTranslation();
  const [modal, contextHolder] = Modal.useModal();

  const deleteMutation = useDeleteBackExportTemplateId();
  const queryClient = useQueryClient();

  const handleClose = useCallback(() => {
    if (!templateId) {
      // biome-ignore format: <tbd>
      message.error(t("export-template-modal-button.unsaved-template-miss-id", `Il Template deve essere salvato per poter essere eliminato.`));
      return;
    }

    modal.confirm({
      type: 'error',
      okType: 'danger',
      icon: (
        <WarningOutlined
          aria-hidden="true"
          style={{ color: token.colorError }}
        />
      ),
      title: t(
        'export-template-modal-button.sei-sicuro-di-voler-eliminare-il-template',
        'Sei sicuro di voler eliminare il template?',
      ),
      okText: t('export-template-modal-button.elimina', 'Elimina'),
      cancelText: t('export-template-modal-button.chiudi', 'Chiudi'),
      async onOk() {
        try {
          await deleteMutation.mutateAsync({ id: templateId! });
          await queryClient.invalidateQueries(
            getGetBackExportTemplateTypeQueryKey(exportTemplateType!),
          );
          setSelectedTemplate('');
          onDelete([]);
          message.success(
            t(
              'export-template-modal-button.template-eliminato-correttamente',
              'Template eliminato correttamente.',
            ),
          );
        } catch (e) {
          console.error(
            t(
              'export-template-modal-button.errore-nel-eliminazione-del-template',
              `Errore nell'eliminazione il template`,
            ),
            { e },
          );
          // biome-ignore format: <tbd>
          message.error(getNetworkErrorMessage(e, t("export-template-modal-button.error.errore-nel-eliminazione-del-template",`Si è verificato un errore durante l'eliminazione del template'.`)));
        }
      },
      onCancel() {},
    });
  }, [token.colorError, templateId, t, modal]);

  return (
    <>
      {contextHolder}
      <Tooltip
        title={t(
          'export-template-modal-button.elimina-il-template',
          'Elimina questo Template',
        )}
      >
        <Button
          danger
          disabled={isJustCreated ?? !templateId}
          onClick={() => handleClose()}
          icon={<DeleteOutlined aria-hidden="true" />}
          {...otherProps}
        />
      </Tooltip>
    </>
  );
}
